import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { InlineSVGService } from 'ng-inline-svg/lib_commonjs/inline-svg.service';
import { Shirt } from 'src/app/model/out-fit';
import {
  ValidationErrorKey,
  ValidationResult,
} from 'src/app/validations/customization/customization.validation';

@Component({
  selector: 'app-customize-shirt',
  templateUrl: './customize-shirt.component.html',
  styleUrls: ['./customize-shirt.component.css'],
})
export class CustomizeShirtComponent {
  @Input() colors!: string[];
  collarTypes = [
    'C1',
    'C2',
    'C3',
    'C4',
    'C5',
    'C6',
    'C7',
    'C8',
    'C9',
    'C10',
    'C11',
    'C12',
    'C13',
    'C14',
    'C15',
    'C16',
    'C17',
    'C19',
  ];

  @Input() shirtSelections!: Shirt;
  @Output() shirtSelectionsChange = new EventEmitter<any>();
  @ViewChild('svgContainer') svgContainer!: ElementRef;

  @Input() validationResult: ValidationResult | null = null;

  constructor(
    private renderer: Renderer2,
    private inlineSVGService: InlineSVGService,
    private http: HttpClient,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shirtSelections']) {
      setTimeout(() => this.updateShirt());
    }
  }
  hasError(key: ValidationErrorKey): boolean {
    return !!this.validationResult?.errorKeys.includes(key);
  }

  applyJersyType(type: string) {
    if (this.shirtSelections.jersyType !== type) {
      this.shirtSelections.jersyType = type;
      this.shirtSelectionsChange.emit({ part: 'jersyType', value: type });
      setTimeout(() => this.updateShirt());
    }
  }

  applyColorToSelector(color: string, selectors: string[]) {
    const finalColor = color?.trim() || ''; // ✅ force empty string if undefined/null
    selectors.forEach((selector) => {
      const elements =
        this.svgContainer?.nativeElement.querySelectorAll(selector);
      elements?.forEach((el: Element) => {
        this.renderer.setAttribute(el, 'fill', finalColor);
      });
    });
  }

  applyCollarColor(color: string) {
    const finalColor = color?.trim() || '';
    this.shirtSelections.collarColor = finalColor;
    this.shirtSelectionsChange.emit({ part: 'collarColor', value: finalColor });
    this.applyColorToSelector(finalColor, [
      '#collar',
      '#collarType svg path:not(#front_collar)',
    ]);
  }

  applyFrontCollarColor(color: string) {
    const finalColor = color?.trim() || '#FFFFFF';
    this.shirtSelections.FrontCollarColor = finalColor;
    this.shirtSelectionsChange.emit({
      part: 'FrontCollarColor',
      value: finalColor,
    });
    this.applyColorToSelector(finalColor, ['#front_collar']);
  }

  async changeCollarType(collarType: string) {
    this.shirtSelections.collarType = collarType;
    this.shirtSelectionsChange.emit({ part: 'collarType', value: collarType });

    try {
      const newCollar = await this.http
        .get(`assets/img/customize/${collarType}.svg`, { responseType: 'text' })
        .toPromise();

      const svgMain = this.svgContainer.nativeElement.querySelector('svg');
      const collarElement = svgMain?.querySelector('#collarType');

      if (collarElement && newCollar) {
        collarElement.innerHTML = newCollar;

        const innerSvg = collarElement.querySelector('svg');
        if (innerSvg) {
          innerSvg.setAttribute('overflow', 'visible');
          innerSvg.style.overflow = 'visible';
          this.renderer.setStyle(innerSvg, 'border', '2px solid #1A1A18');
          this.renderer.setStyle(innerSvg, 'box-sizing', 'border-box');
        }
      }

      this.applyCollarColor(this.shirtSelections.collarColor || '');
      if (this.shirtSelections.FrontCollarColor) {
        this.applyFrontCollarColor(this.shirtSelections.FrontCollarColor);
      }
    } catch (error) {
      console.error('Failed to change collar:', error);
    }
  }

  applyBodyColor(color: string) {
    this.shirtSelections.bodyColor = color;
    this.shirtSelectionsChange.emit({ part: 'bodyColor', value: color });
    this.applyColorToSelector(color, ['#body']);
  }

  applyShoulderColor(color: string) {
    this.shirtSelections.shoulderColor = color;
    this.shirtSelectionsChange.emit({ part: 'shoulderColor', value: color });
    this.applyColorToSelector(color, ['#shoulder']);
  }

  toggleSleeve(isSleeve: boolean) {
    this.shirtSelections.isSleeve = isSleeve;
    this.shirtSelectionsChange.emit({
      part: 'shirtSelections',
      value: isSleeve,
    });
    const display = isSleeve ? 'block' : 'none';
    const paths = this.svgContainer?.nativeElement.querySelectorAll('#sleeve');
    paths?.forEach(
      (path: any) => ((path as HTMLElement).style.display = display),
    );
  }

  applySleeveColor(color: string) {
    this.shirtSelections.sleeveColor = color;
    this.shirtSelectionsChange.emit({ part: 'sleeveColor', value: color });
    this.applyColorToSelector(color, ['#sleeve']);
  }

  applyLogoColor(color: string) {
    this.shirtSelections.logoColor = color;
    this.shirtSelectionsChange.emit({ part: 'logoColor', value: color });
    this.applyColorToSelector(color, ['#logo']);
  }

  applyShoulderLogoColor(color: string) {
    this.shirtSelections.shoulderLogoColor = color;
    this.shirtSelectionsChange.emit({
      part: 'shoulderLogoColor',
      value: color,
    });
    this.applyColorToSelector(color, ['#shoulder_logo']);
  }

  uploadLogo(e: any) {
    let urlpath: string;
    if (typeof e === 'string' && e !== '') {
      urlpath = e;
    } else {
      urlpath = URL.createObjectURL(e.target.files[0]);
      this.shirtSelections.logoFile = e.target.files[0];
    }

    this.shirtSelections.logoPath = urlpath;
    this.shirtSelectionsChange.emit({ part: 'logoPath', value: urlpath });
    this.shirtSelectionsChange.emit({
      part: 'logoFile',
      value: e.target.files[0],
    });
  }

  updateShirt() {
    this.changeCollarType(this.shirtSelections.collarType);
    this.applyJersyType(this.shirtSelections.jersyType);
    this.applyCollarColor(this.shirtSelections.collarColor || '');
    this.applyBodyColor(this.shirtSelections.bodyColor || '');
    this.applyShoulderColor(this.shirtSelections.shoulderColor || '');
    this.applySleeveColor(this.shirtSelections.sleeveColor || '');
    this.toggleSleeve(this.shirtSelections.isSleeve);
    this.applyLogoColor(this.shirtSelections.logoColor || '');
    this.applyShoulderLogoColor(this.shirtSelections.shoulderLogoColor || '');
    this.applyFrontCollarColor(this.shirtSelections.FrontCollarColor || '');
  }
}
